import { RawUser } from "../types";
import { UserRole } from "../types/models";
import get from "lodash.get";
import { SubscriptionPlanType } from "../types/models/subscription";
import { QuiltySubscriptionPlanType } from "../types/models/quiltySubscription";

export const hasRole = (user: { role: UserRole }, role: UserRole): boolean =>
    (user.role || "").toLowerCase() === role.toLowerCase();

export const isAdmin = (user: { role: UserRole } | null | undefined): boolean =>
    Boolean(user && (hasRole(user, UserRole.Admin) || hasRole(user, UserRole.Assistant)));

export const isThirdpartyAdmin = (user: { role: UserRole } | null | undefined): boolean =>
    Boolean(user && hasRole(user, UserRole.ThirdpartyAdmin));

export interface OrganizationAccessControl {
    overview: {
        description: boolean;
        selfDescription: boolean;
        similarCompanies: boolean;
    };
    sidebar: {
        social: boolean;
        contact: boolean;
    };
    technical: {
        technicalAssessment: boolean;
        production: boolean;
    };
    financial: {
        kpi: boolean;
        transactions: boolean;
        downloads: boolean;
    };
    quiltyResources: boolean;
    teamOwnership: {
        teamOwnership: boolean;
    };
    projectsPartners: {
        projects: boolean;
        partners: boolean;
    };
    newsEvents: {
        news: boolean;
        events: boolean;
    };
}

const noOrganizationAccess: OrganizationAccessControl = {
    overview: {
        description: false,
        selfDescription: false,
        similarCompanies: false,
    },
    sidebar: {
        social: false,
        contact: false,
    },
    technical: {
        technicalAssessment: false,
        production: false,
    },
    financial: {
        kpi: false,
        transactions: false,
        downloads: false,
    },
    quiltyResources: false,
    teamOwnership: {
        teamOwnership: false,
    },
    projectsPartners: {
        projects: false,
        partners: false,
    },
    newsEvents: {
        news: false,
        events: false,
    },
};

export const organizationAccess = (user: RawUser | null): OrganizationAccessControl => {
    if (!user) {
        return noOrganizationAccess;
    }

    return {
        overview: {
            selfDescription: true,
            description: hasEnterpriseSubscription(user),
            similarCompanies: hasEnterpriseSubscription(user),
        },
        sidebar: {
            social: true,
            contact: true,
        },
        technical: {
            technicalAssessment: hasEnterpriseSubscription(user),
            production: hasEnterpriseSubscription(user),
        },
        financial: {
            kpi:
                hasQuiltyBasicSubscription(user) ||
                hasQuiltyIntelligenceSubscription(user) ||
                hasQuiltyEnterpriseSubscription(user),
            transactions: true,
            downloads: true,
        },
        quiltyResources:
            hasQuiltyBasicSubscription(user) ||
            hasQuiltyIntelligenceSubscription(user) ||
            hasQuiltyEnterpriseSubscription(user),
        teamOwnership: {
            teamOwnership: hasEnterpriseSubscription(user),
        },
        projectsPartners: {
            projects: hasEnterpriseSubscription(user),
            partners: hasEnterpriseSubscription(user),
        },
        newsEvents: {
            news: true,
            events: true,
        },
    };
};

export const subscriptionRequired = () => ({
    platform: {
        explorerTool: SubscriptionPlanType.Enterprise,
    },
    organization: {
        overview: {
            description: SubscriptionPlanType.Enterprise,
            similarCompanies: SubscriptionPlanType.Enterprise,
        },
        technical: {
            technicalAssessment: SubscriptionPlanType.Enterprise,
            production: SubscriptionPlanType.Enterprise,
        },
        financial: {
            kpi: QuiltySubscriptionPlanType.Basic,
        },
        quiltyResources: QuiltySubscriptionPlanType.Enterprise,
        teamOwnership: {
            teamOwnership: SubscriptionPlanType.Enterprise,
        },
        projectsPartners: {
            projects: SubscriptionPlanType.Enterprise,
            partners: SubscriptionPlanType.Enterprise,
        },
    },
});

export const getSubscriptionRequired = (
    field: string,
): SubscriptionPlanType | QuiltySubscriptionPlanType | undefined => {
    const requestedAccess: boolean | any = get(subscriptionRequired(), field);

    if (typeof requestedAccess === "object") {
        // TODO: return all nested subscriptions needed
        return undefined;
    }

    return requestedAccess;
};

export const hasOrganizationAccess = (
    user: RawUser | null,
    field: string,
    organizationId?: string,
): boolean => {
    if (user?.organization && user.organization.organizationid === organizationId) {
        return true;
    }

    const requestedAccess: boolean | any = get(
        organizationAccess(user),
        field,
        noOrganizationAccess,
    );

    if (typeof requestedAccess === "object") {
        return Object.values(requestedAccess).some((prop) => prop);
    }

    return requestedAccess;
};

export const isSubscribed = (user: RawUser | null): boolean => Boolean(user && user.subscription);

export const isQuiltySubscribed = (user: RawUser | null): boolean =>
    Boolean(user && user.quiltySubscription);

export const hasActiveSubscription = (user: RawUser | null): boolean =>
    isSubscribed(user) &&
    !!user!.subscription!.endsAt &&
    new Date(user!.subscription!.endsAt) > new Date();

export const hasActiveQuiltySubscription = (user: RawUser | null): boolean =>
    isQuiltySubscribed(user) &&
    !!user!.quiltySubscription!.endsAt &&
    new Date(user!.quiltySubscription!.endsAt) > new Date();

export const hasIndividualSubscription = (user: RawUser | null): boolean =>
    isAdmin(user) ||
    (hasActiveSubscription(user) && user?.subscription?.plan === SubscriptionPlanType.Individual);
export const hasEnterpriseSubscription = (user: RawUser | null): boolean =>
    isAdmin(user) ||
    (hasActiveSubscription(user) && user?.subscription?.plan !== SubscriptionPlanType.Individual);

export const getGseSubscription = (user: RawUser | null): SubscriptionPlanType | string => {
    switch (user?.subscription?.plan) {
        case SubscriptionPlanType.Individual:
            return SubscriptionPlanType.Individual;

        case SubscriptionPlanType.Enterprise:
            return SubscriptionPlanType.Enterprise;

        default:
            return "no subscription";
    }
};

export const hasSubscription = (
    user: RawUser | null,
    subscription: SubscriptionPlanType | QuiltySubscriptionPlanType,
): boolean => {
    if (isAdmin(user)) {
        return true;
    }

    return (
        (typeof subscription === typeof SubscriptionPlanType &&
            hasActiveSubscription(user) &&
            user?.subscription?.plan === subscription) ||
        (typeof subscription === typeof QuiltySubscriptionPlanType &&
            isThirdpartyAdmin(user) &&
            hasActiveQuiltySubscription(user) &&
            user?.quiltySubscription?.plan === subscription)
    );
};

export const hasQuiltyBasicSubscription = (user: RawUser | null): boolean =>
    (hasActiveQuiltySubscription(user) &&
        user?.quiltySubscription?.plan === QuiltySubscriptionPlanType.Basic) ||
    isThirdpartyAdmin(user) ||
    isAdmin(user);

export const hasQuiltyIntelligenceSubscription = (user: RawUser | null): boolean =>
    (hasActiveQuiltySubscription(user) &&
        user?.quiltySubscription?.plan === QuiltySubscriptionPlanType.Intelligence) ||
    isThirdpartyAdmin(user) ||
    isAdmin(user);

export const hasQuiltyEnterpriseSubscription = (user: RawUser | null): boolean =>
    (hasActiveQuiltySubscription(user) &&
        user?.quiltySubscription?.plan === QuiltySubscriptionPlanType.Enterprise) ||
    isThirdpartyAdmin(user) ||
    isAdmin(user);

export const getQuiltySubscription = (
    user: RawUser | null,
): QuiltySubscriptionPlanType | string => {
    switch (user?.quiltySubscription?.plan) {
        case QuiltySubscriptionPlanType.Basic:
            return QuiltySubscriptionPlanType.Basic;

        case QuiltySubscriptionPlanType.Intelligence:
            return QuiltySubscriptionPlanType.Intelligence;

        case QuiltySubscriptionPlanType.Enterprise:
            return QuiltySubscriptionPlanType.Enterprise;

        default:
            return "no subscription";
    }
};

export const MAX_FREE_ORGANIZATION_SEARCH = 50;
